import { FaEraser, FaSearch } from "react-icons/fa";
import Button from "../../../../components/button/button";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import { Template } from "../../../../components/template/template";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import ContactsService from "../../../../services/web/contacts-service";

export default function SharedAgents() {
    const [searchParams] = useSearchParams();
    const ibge = useMemo(() => searchParams.get("ibge"), [searchParams]);
    
    const initialFilter = useMemo(() => ({
        filter: [],
        page: 1,
        size: 500,
        ibge,
        sort: [{ field: "nameWar", dir: "asc" }]
    }), [ibge]);

    const [nameWar, setNameWar] = useState("");
    const [phone, setPhone] = useState("");
    const [city, setCity] = useState("");
    const [filter, setFilter] = useState(initialFilter);
    
    const { data: agents = [], isLoading } = useQuery({
        queryKey: ['agents', filter],
        queryFn: () => ContactsService.agents(filter),
        select: (res) => res?.data?.data ?? [],
    });

    const focalPoints = {
        DISPATCH: "Atendimento e Despacho",
        INTELLIGENCE: "Inteligência",
        MONITORING: "Monitoramento",
    };

    const formatFocalPoints = (v) => {
        if (!Array.isArray(v) || v.length === 0) return "";
        return v.map((k) => focalPoints[k] ?? k).join(", ");
    };

    const columnDefs = useMemo(() => ([
        { headerName: "Nome", field: "nameWar", flex: 1},
        { headerName: "Telefone", field: "phone", flex: 1, sortable: true, comparator: AgGridUtils.nullLastComparator },
        { headerName: "E-mail", field: "email", flex: 1, tooltipField: "email", sortable: true, comparator: AgGridUtils.nullLastComparator },
        { headerName: "Município", field: "city", flex: 1, sortable: true, comparator: AgGridUtils.nullLastComparator ,
            cellRenderer: (p) => {
                const rowIbge = p.data?.ibge;
                if (!rowIbge) return p.value ?? "";
                return (
                    <a
                        style={{ textDecoration: "underline", cursor: "pointer", color: "inherit" }}
                        onClick={() => window.open(ContactsService.institutionsIbge(rowIbge))}
                    >
                        {p.value}
                    </a>
                )
            }
        },
        { headerName: "Ponto focal", field: "focalPoints", flex: 1, sortable: true, comparator: AgGridUtils.nullLastComparator, 
            valueGetter: (p) => formatFocalPoints(p.data?.focalPoints),
            tooltipValueGetter: (p) => formatFocalPoints(p.data?.focalPoints),
        },
    ]), []);

    const handleSearch = () => {
        setFilter((prev) => ({
            ...prev, 
            page: 1, 
            ibge,
            filter: [
                { field: "nameWar", value: nameWar?.trim() },
                { field: "phone", value: phone?.trim() },
                { field: "city", value: city?.trim() },
            ].filter((i) => i.value),
        }));
    };

    const handleClear = () => {
        setNameWar("");
        setPhone("");
        setCity("");
        setFilter(initialFilter);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>Agentes</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm 
                                id="name"
                                label="Nome"
                                classNameWrapper="flex-1"
                                value={nameWar}
                                onChange={(e) => setNameWar(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm 
                                id="phone"
                                label="Telefone"
                                classNameWrapper="flex-1"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                onKeyDown={handleKeyDown}
                                mask={[{ mask: "(00) 0000-0000" }, { mask: "(00) 00000-0000" }]}
                            />
                            <InputForm 
                                id="city"
                                label="Município"
                                classNameWrapper="flex-1"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact 
                                rowData={agents}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                                enableBrowserTooltips={true}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}